import { Box, Flex, Input, Menu, Text, ScrollArea, Tooltip, Loader } from '@mantine/core';
import { createStyles } from '@mantine/emotion';
import { useMemo, useRef, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { TbCheck, TbChevronDown, TbChevronRight, TbInfoCircle, TbSearch } from 'react-icons/tb';
import { idCreator } from '../../../Project/utils/idCreator';

const useStyles = createStyles(theme => ({
  select: {
    border: `1px solid ${theme.colors.gray[1]}`,
    borderRadius: '4px',
    minWidth: '183px',
    cursor: 'pointer'
  }
}));

const statusIcon = {
  completed: <TbCheck color="#2F9E44" fontSize="20px" style={{ minWidth: '20px' }} />,
  'in progress': <Loader color="blue" size="xs" />,
  failed: <TbInfoCircle color="#FA5252" fontSize="20px" style={{ minWidth: '20px' }} />
};

export function SearchDropdown({
  idPrefix,
  label,
  value,
  options = [],
  placeholder,
  onChange,
  disabledOptions = [],
  hasSearch = false,
  color,
  hasInfoIcon = false,
  renderOptions,
  statuses = {},
  isLoading = false,
  sortByStatus = false,
  dropdownFooter,
  disabled = false,
  subMenuGroup = null
}) {
  const rootId = idPrefix || 'data-extraction-search-dropdown';
  const { classes } = useStyles();
  const [term, setTerm] = useState();
  const boxRef = useRef();
  const [opened, { open, close }] = useDisclosure(false);
  const filteredOptions = useMemo(() => {
    if (!options || options.length === 0) return [];

    const filtered = !term
      ? options
      : options.filter(item => item.name.toLowerCase().includes(term.toLowerCase()));

    if (!sortByStatus) return filtered;

    // sort the dropdown list based on processing status when opt-in:
    // processed items will be shown on top of the dropdown list
    return [...filtered].sort((a, b) => {
      const aProcessed = !!statuses[a.id];
      const bProcessed = !!statuses[b.id];
      if (aProcessed === bProcessed) return 0;
      return aProcessed ? -1 : 1;
    });
  }, [options, term, statuses, sortByStatus]);

  const filteredSubGroupItems = useMemo(() => {
    if (!subMenuGroup?.items?.length) return [];
    if (!term) return subMenuGroup.items;
    return subMenuGroup.items.filter(item => item.name.toLowerCase().includes(term.toLowerCase()));
  }, [subMenuGroup, term]);

  const onChangeTerm = e => {
    setTerm(e.target.value);
  };

  const defaultRenderOptions = item => (
    <Menu.Item
      id={`${rootId}-option-${item.id || idCreator(item.name)}`}
      onClick={() => onChange(item)}
      key={item.id || item.name}
      disabled={disabledOptions.includes(item.id || item.name)}
      w="100%"
    >
      <Flex align="center" justify="space-between">
        <Tooltip label={item.name} disabled={!item.name}>
          <Text
            id={`${rootId}-option-name-${item.id || idCreator(item.name)}`}
            fz="14px"
            lh="20px"
            lineClamp={2}
          >
            {item.name}
          </Text>
        </Tooltip>
        {!!statuses[item.id] && statusIcon[statuses[item.id]]}
        {hasInfoIcon && (
          <TbInfoCircle fontSize="16px" color="#228BE6" style={{ minWidth: '16px' }} />
        )}
      </Flex>
    </Menu.Item>
  );

  return (
    <Menu
      id={`${rootId}-menu`}
      zIndex={100000}
      shadow="md"
      width={200}
      opened={!disabled && opened}
      onOpen={open}
      onClose={close}
    >
      <Menu.Target>
        <Box id={`${rootId}-root`} onClick={disabled ? undefined : open}>
          <Text id={`${rootId}-label`} mb="3" fz="12px" fw="600" color="#25262B">
            {label}
          </Text>
          <Flex
            id={`${rootId}-trigger`}
            align="center"
            justify="space-between"
            className={classes.select}
            p="2.5px 12px"
            maw="240px"
            sx={{
              gap: '8px',
              border: !value || color ? `1px solid ${color}` : `1px solid #CED4DA`,
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
              pointerEvents: disabled ? 'none' : undefined
            }}
            gap="8px"
          >
            <Tooltip disabled={value?.name?.length < 38 || !value?.name} label={value?.name}>
              <Text
                id={`${rootId}-value`}
                fz="12px"
                lh="17px"
                style={{
                  color: color ? color : value?.name ? '#000' : '#909296'
                }}
                truncate="end"
              >
                {value?.name || placeholder}
              </Text>
            </Tooltip>
            <Flex miw="12px">
              {isLoading ? (
                <Loader size={12} />
              ) : (
                <TbChevronDown size={12} color={color ? color : value?.name ? '#000' : '#909296'} />
              )}
            </Flex>
          </Flex>
        </Box>
      </Menu.Target>

      <Menu.Dropdown p="8px" w="max-content" miw="220px" maw="420px">
        {hasSearch && (
          <Input
            id={`${rootId}-search-input`}
            placeholder="Search"
            leftSection={<TbSearch size={16} />}
            size="xs"
            mb="8px"
            onChange={onChangeTerm}
            value={term}
          />
        )}
        <ScrollArea.Autosize scrollbarSize={14} scrollHideDelay={500} mah="58vh">
          {filteredOptions.map(item =>
            renderOptions ? renderOptions(item) : defaultRenderOptions(item)
          )}
          {subMenuGroup && filteredSubGroupItems.length > 0 && (
            <Menu
              id={`${rootId}-submenu-menu`}
              shadow="md"
              trigger="hover"
              position="right-start"
              withinPortal
              openDelay={100}
              closeDelay={150}
            >
              <Menu.Target>
                <Menu.Item
                  id={`${rootId}-submenu-item`}
                  rightSection={<TbChevronRight size={14} />}
                >
                  {subMenuGroup.label}
                </Menu.Item>
              </Menu.Target>
              <Menu.Dropdown maw="420px" miw="220px" p="8px">
                <ScrollArea.Autosize scrollbarSize={14} scrollHideDelay={500} mah="58vh">
                  {filteredSubGroupItems.map(item => subMenuGroup.renderItem(item))}
                </ScrollArea.Autosize>
              </Menu.Dropdown>
            </Menu>
          )}
        </ScrollArea.Autosize>

        {dropdownFooter}
      </Menu.Dropdown>
    </Menu>
  );
}
